import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
// components
import InnerImageZoom from 'react-inner-image-zoom';
import 'react-inner-image-zoom/lib/styles.min.css';
import Modal from 'react-modal';
// common components
import { Icon, Text } from '../common';
import { fontSize, lineHeight } from '@mui/system';
 
// Ensure modal is accessible
Modal.setAppElement('#root');
 
const ImageQuestionMedia = React.memo(({ url, mediaTimer, sectionDescription, hasViewed, onViewComplete, isNote}) => {
  //hook:useState Initialize state for modal visibility and timer
  const [isModalOpen, setModalOpen] = useState(false);
  const [timer, setTimer] = useState(mediaTimer ?? 10);
  const [isViewingComplete, setIsViewingComplete] = useState(false);
  // Reset timer when modal opens
  useEffect(() => {
    if (isModalOpen && !hasViewed) {
      setTimer(mediaTimer ?? 10);
      setIsViewingComplete(false);
    }
  }, [isModalOpen, mediaTimer, hasViewed]);
 
  useEffect(() => {
  if (!isModalOpen) return;
 
  const scrollY = window.scrollY || window.pageYOffset || 0;
 
  const originalBodyOverflow = document.body.style.overflow;
  const originalBodyPosition = document.body.style.position;
  const originalBodyTop = document.body.style.top;
  const originalBodyWidth = document.body.style.width;
 
  const originalHtmlOverflow = document.documentElement.style.overflow;
 
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.width = '100%';
 
  document.documentElement.style.overflow = 'hidden';
 
  return () => {
    document.body.style.overflow = originalBodyOverflow;
    document.body.style.position = originalBodyPosition;
    document.body.style.top = originalBodyTop;
    document.body.style.width = originalBodyWidth;
 
    document.documentElement.style.overflow = originalHtmlOverflow;
 
    window.scrollTo(0, scrollY);
  };
}, [isModalOpen]);
 
  useEffect(() => {
    let interval;
    if (isModalOpen && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsViewingComplete(true);
            // onViewComplete()
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isModalOpen, timer, onViewComplete]);
  //function:openModal This function opens the modal and resets the timer
  const openModal = () => {
    if (hasViewed) return;
    // setTimer(mediaTimer ?? 10);
    setModalOpen(true);
  };
  //function:closeModal This function closes the modal and resets the timer
  const closeModal = () => {
    onViewComplete();
    setModalOpen(false);
    // setTimer('');
  };
  //function:handleLoadedMetadata This function sets the video duration when the video metadata is loaded
  const handleLoadedMetadata = event => {
    const duration = Math.ceil(event.target.duration); // round up to nearest sec
    setTimer(duration);
  };
  // Utility: Convert seconds to mm:ss format
  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const [visualViewportState, setVisualViewportState] = useState(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    return {
      offsetTop: vv?.offsetTop ?? 0,
      offsetLeft: vv?.offsetLeft ?? 0,
      width: vv?.width ?? (typeof window !== 'undefined' ? window.innerWidth : 0),
      height: vv?.height ?? (typeof window !== 'undefined' ? window.innerHeight : 0),
      scale: vv?.scale ?? 1
    };
  });
 
  useEffect(() => {
    if (!isModalOpen) return;
    const vv = window.visualViewport;
    if (!vv) return;
 
    const update = () => {
      setVisualViewportState({
        offsetTop: vv.offsetTop,
        offsetLeft: vv.offsetLeft,
        width: vv.width,
        height: vv.height,
        scale: vv.scale
      });
    };
 
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [isModalOpen]);
 
  const hud = isModalOpen
    ? ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10010,
            pointerEvents: 'none'
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: visualViewportState.offsetTop,
              left: visualViewportState.offsetLeft,
              width: visualViewportState.width,
              height: visualViewportState.height,
              pointerEvents: 'none'
            }}
          >
            {timer > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(20px + env(safe-area-inset-top, 0px))',
                  right: 'calc(15px + env(safe-area-inset-right, 0px))',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#D9D9D9',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  transform: `scale(${1 / (visualViewportState.scale || 1)})`,
                  transformOrigin: 'top right',
                  pointerEvents: 'none'
                }}
              >
                {formatTime(timer)}
              </div>
            )}
 
            {timer === 0 && (
              <button
                type="button"
                onClick={closeModal}
                aria-label="Close"
                style={{
                  position: 'absolute',
                  top: 'calc(16px + env(safe-area-inset-top, 0px))',
                  right: 'calc(16px + env(safe-area-inset-right, 0px))',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: '#E53935',
                  border: 'none',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backdropFilter: 'blur(4px)',
                  transform: `scale(${1 / (visualViewportState.scale || 1)})`,
                  transformOrigin: 'top right',
                  pointerEvents: 'auto'
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  focusable="false"
                  
                >
                  <line
                    x1="6"
                    y1="6"
                    x2="18"
                    y2="18"
                    stroke="#fff"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <line
                    x1="18"
                    y1="6"
                    x2="6"
                    y2="18"
                    stroke="#fff"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>,
        document.body
      )
    : null;
  if (!url) return null;
  const isVideo = /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);
  //UI
  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
      </div>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '200px',
          marginBottom: '1rem',
          borderRadius: '12px',
          overflow: 'hidden'
        }}
      >
        {isVideo ? (
          <video
            src={url}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '12px'
            }}
          />
        ) : (
          <img
            src={url}
            alt="Zoomable"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '15px',
              display: 'block',
              padding: '0.5rem',
              borderWidth: '1px',
              borderColor: '#D9D9D9',
              borderStyle: 'solid'
            }}
          />
        )}
        {/* Tap to View Button */}
        <button
          onClick={openModal}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(255,255,255,0.9)',
            color: hasViewed ? 'var(--primaryColor)' : '#000000',
            padding: '10px 20px',
            borderRadius: '5px',
            border: '1px solid',
            borderColor: '#000000',
            cursor: hasViewed ? 'default' : 'pointer',
            fontSize: '18px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {hasViewed ? (
            <>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" stroke="var(--primaryColor)" strokeWidth="2" fill="white" />
              <text x="12" y="16" textAnchor="middle" fill="var(--primaryColor)" fontSize="12" fontWeight="bold" paddingLeft="10">✔</text>
              </svg>
                 Viewed
            </>
          ) : (
            'Tap to View'
          )}
        </button>
      </div>
      {/* Red Note */}
      {isNote && (
        <div style={{ paddingBottom: '0.4rem' }}>
        <Text style={{ fontSize: 12, lineHeight: 16 }} color={'#FF1010'}>
          Note : {isVideo ? ' Watch the video' : 'See the Image'} first to answer the following question
        </Text>
      </div>
      )}
      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        disabled={hasViewed}
        onRequestClose={closeModal}
        contentLabel="Zoomed Image"
        style={{
          content: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            padding: 0,
            border: 'none',
            borderRadius: 0,
            width: '100vw',
            height: '100vh',
            overflow: 'hidden',
            backgroundColor: '#515151'
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 9999
          }
        }}
      >
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          {hud}
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              overflow: 'auto'
            }}
          >
            <div
              style={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isVideo ? (
                <video
                  src={url}
                  autoPlay
                  playsInline
                  controls={false}
                  onLoadedMetadata={handleLoadedMetadata}
                  style={{
                    width: '100%',
                    maxHeight: '50vh',
                    objectFit: 'contain',
                    borderRadius: '12px'
                  }}
                />
              ) : (
                <InnerImageZoom
                  src={url}
                  zoomSrc={url}
                  zoomType="click"
                  zoomPreload
                  hideHint
                  alt="Zoomable"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    borderRadius: '12px'
                  }}
                />
              )}
            </div>
            {/* 👉 Bottom text visible always */}
           {timer != 0 && <div style={{ marginBottom: '45px', textAlign: 'center' }}>
              <Text color={'#fff'}>Pinch the {isVideo ? 'video' : 'image'} to zoom in and out.</Text>
            </div>}
          </div>
        </div>
      </Modal>
      {/* 🧼 Extra CSS to hide zoom icon */}
      <style>
        {`
          .inner-image-zoom__zoom-btn {
            display: none !important;
          }
        `}
      </style>
    </>
  );
});
 
export default ImageQuestionMedia;
